import { useQuery } from '@tanstack/react-query';
import {
  DataTable,
  StatusChip,
  EmptyState,
  SkeletonLoader,
  type DataTableColumn,
} from '@shared/design-system/components';
import type { StatusKind } from '@shared/design-system/tokens';
import {
  getSupportSiteHealth,
  listSupportComponents,
  listSupportSites,
  type SupportComponentSummary,
  type SupportSiteHealth,
  type SupportSiteSummary,
  type WorkerStatus,
} from '../../api/supportClient';

/**
 * Pane 1 (hardening plan Step 7): fleet-wide inventory & health,
 * PROGRESS-based not liveness-based per the plan's own instruction --
 * this pane renders exactly what `/support/sites` and
 * `/support/components` report, and both of those endpoints are
 * explicit about what they can and can't see yet
 * (`support.py::list_support_components`'s own docstring: full
 * per-component liveness needs Phase 2's recovery journal read
 * cross-site, which isn't wired yet -- only "worker" is real today).
 * Rather than pretend otherwise, unreported components are simply
 * absent from the table, not synthesized as "unknown"/fake rows.
 */

function workerStatusToStatusKind(status: WorkerStatus): StatusKind {
  switch (status) {
    case 'ready':
      return 'active';
    case 'initialising':
      return 'warning';
    case 'stale':
      return 'warning';
    case 'unreachable':
    default:
      return 'error';
  }
}

function componentStatusToStatusKind(status: SupportComponentSummary['status']): StatusKind {
  if (status === 'healthy') return 'active';
  if (status === 'degraded') return 'warning';
  return 'error';
}

/** Amber/red thresholds are the same ones a support engineer would
 *  reach for `docker stats` to check anyway -- not tied to any SLA,
 *  just "worth a glance" vs "worth a look now". */
function pctStatusKind(pct: number | null): StatusKind | null {
  if (pct === null) return null;
  if (pct >= 90) return 'error';
  if (pct >= 75) return 'warning';
  return 'active';
}

function HealthMetric({ label, pct }: { label: string; pct: number | null }) {
  const kind = pctStatusKind(pct);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
      <span style={{ color: 'var(--nx-muted)', minWidth: 30 }}>{label}</span>
      {pct === null || kind === null ? (
        <span style={{ color: 'var(--nx-muted)' }}>—</span>
      ) : (
        <StatusChip status={kind} label={`${pct}%`} />
      )}
    </div>
  );
}

export function InventoryPane() {
  const sitesQuery = useQuery({
    queryKey: ['support', 'sites'],
    queryFn: listSupportSites,
    refetchInterval: 20_000,
  });
  const componentsQuery = useQuery({
    queryKey: ['support', 'components'],
    queryFn: () => listSupportComponents(),
    refetchInterval: 20_000,
  });
  // Its own query, on its own (short) interval. This is the only
  // genuinely live data on the pane -- CPU/memory move continuously,
  // so a slow poll makes them look frozen. Kept separate from the site
  // list so a ~2s broker fan-out never delays the table painting, and
  // `refetchIntervalInBackground: false` (the default) means a console
  // left open on another tab stops sampling every site's broker.
  const healthQuery = useQuery({
    queryKey: ['support', 'site-health'],
    queryFn: getSupportSiteHealth,
    refetchInterval: 15_000,
  });

  if (sitesQuery.isLoading) {
    return <SkeletonLoader shape="table-row" count={5} />;
  }

  if (sitesQuery.isError) {
    return (
      <EmptyState
        title="Could not load site inventory"
        description={sitesQuery.error instanceof Error ? sitesQuery.error.message : 'Unknown error'}
      />
    );
  }

  const sites = sitesQuery.data ?? [];
  const healthBySite = new Map<string, SupportSiteHealth>(
    (healthQuery.data?.sites ?? []).map((h) => [h.site, h]),
  );
  const componentsBySite = new Map<string, SupportComponentSummary[]>();
  for (const c of componentsQuery.data ?? []) {
    const list = componentsBySite.get(c.site) ?? [];
    list.push(c);
    componentsBySite.set(c.site, list);
  }

  const columns: DataTableColumn<SupportSiteSummary>[] = [
    {
      id: 'site',
      header: 'Site',
      cell: (row) => (
        <div>
          <div>{row.label}</div>
          <div style={{ fontSize: 12, color: 'var(--nx-muted)' }}>{row.site}</div>
        </div>
      ),
      sortable: true,
      sortValue: (row) => row.label,
    },
    {
      id: 'worker',
      header: 'Worker',
      cell: (row) => <StatusChip status={workerStatusToStatusKind(row.worker_status)} label={row.worker_status} />,
    },
    {
      id: 'issues-open',
      header: 'Open',
      cell: (row) => row.issue_counts.open,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.issue_counts.open,
    },
    {
      id: 'issues-in-progress',
      header: 'In progress',
      cell: (row) => row.issue_counts.in_progress,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.issue_counts.in_progress,
    },
    {
      id: 'issues-closed',
      header: 'Closed',
      cell: (row) => row.issue_counts.resolved,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.issue_counts.resolved,
    },
    {
      id: 'health',
      header: 'Health',
      cell: (row) => {
        const health = healthBySite.get(row.site);
        // First load of a SEPARATE, slower query -- the rest of the
        // row is already painted, so say "sampling" rather than
        // rendering a dash that reads as "no data".
        if (!health) {
          return (
            <span style={{ color: 'var(--nx-muted)' }}>
              {healthQuery.isLoading ? 'sampling…' : '—'}
            </span>
          );
        }
        // Compose sites only: a row with no `broker_endpoint` is a
        // Kubernetes site since 2026-09-05 and comes back through the
        // site worker instead, so this arm is reached only by a compose
        // deployment that genuinely lacks a broker.
        if (health.status === 'not_configured') {
          return (
            <span style={{ color: 'var(--nx-muted)' }} title="Compose site with no broker_endpoint in its DeploymentBinding">
              no broker configured
            </span>
          );
        }
        // The site WORKER did not answer the health command: the site
        // or the platform bus is the problem, not its broker. Falling
        // through to three grey dashes here would make a dead site look
        // exactly like a healthy Kubernetes one.
        if (health.status === 'worker_unreachable') {
          return (
            <span style={{ color: 'var(--nx-error)' }} title={health.reason ?? 'The site worker did not answer the health command'}>
              worker unreachable
            </span>
          );
        }
        // Distinct from the above on purpose: a broker that is
        // deployed but not answering is a real problem, not an
        // absent feature. `source === 'worker'` means the worker
        // itself reported that its broker gave no usable answer.
        if (health.status === 'unreachable') {
          return (
            <span
              style={{ color: 'var(--nx-error)' }}
              title={
                health.source === 'worker'
                  ? (health.reason ?? 'The site worker reached its broker and got no usable answer')
                  : 'The broker is configured but did not respond in time'
              }
            >
              broker unreachable
            </span>
          );
        }
        // Kubernetes sites answer `ok` with three nulls by construction
        // (the broker reports no cpu/mem/disk there), so the container
        // roll-up is the number that means something; three dashes read
        // as a broken sample.
        const noNumbers = health.cpu_percent == null && health.mem_percent == null && health.disk_percent == null;
        if (noNumbers && health.containers_total != null && health.containers_ok != null) {
          const notOk = health.not_ok && health.not_ok.length > 0 ? health.not_ok.join(', ') : null;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
              <span title={notOk ? `not ok: ${notOk}` : 'reached through the site worker'}>
                <StatusChip
                  status={notOk ? 'warning' : 'active'}
                  label={`${health.containers_ok}/${health.containers_total} containers`}
                />
              </span>
              {notOk && <span style={{ color: 'var(--nx-muted)' }}>not ok: {notOk}</span>}
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <HealthMetric label="CPU" pct={health.cpu_percent} />
            <HealthMetric label="Mem" pct={health.mem_percent} />
            <HealthMetric label="Disk" pct={health.disk_percent} />
          </div>
        );
      },
    },
    {
      id: 'components',
      header: 'Other components',
      cell: (row) => {
        // A FAILED components query must not read as "nothing to
        // report" -- `componentsQuery.data ?? []` renders an empty
        // list either way, which is exactly the silent-wrongness this
        // pane's own header comment claims to avoid (found in review).
        if (componentsQuery.isError) {
          return (
            <span style={{ color: 'var(--nx-error)' }} title={String(componentsQuery.error)}>
              component health unavailable
            </span>
          );
        }
        const others = (componentsBySite.get(row.site) ?? []).filter((c) => c.component !== 'worker');
        if (others.length === 0) {
          return <span style={{ color: 'var(--nx-muted)' }}>not reported yet</span>;
        }
        return (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {others.map((c) => (
              <span key={c.component} title={c.detail ?? undefined}>
                <StatusChip status={componentStatusToStatusKind(c.status)} label={c.component} />
              </span>
            ))}
          </div>
        );
      },
    },
    {
      id: 'core-tier',
      header: 'Core tier (broker)',
      cell: (row) =>
        row.broker_endpoint ? (
          <StatusChip status="active" label="configured" />
        ) : (
          // NOT "no broker configured", which is what this read from
          // 2026-07-26 to 2026-09-09 and was wrong on every Kubernetes
          // site for all of it. ("Two years" was the previous wording
          // and there has never been two years: this repo is 79 days
          // old.) The
          // column is `DeploymentBinding.broker_endpoint`, which is a
          // PLATFORM-REACHABLE address -- and there is none by design:
          // the site NetworkPolicy admits the site's own worker and
          // nothing else. The broker is deployed and running; what it
          // has no is a route from here. Site health and runbook steps
          // both go through the worker instead.
          <StatusChip
            status="active"
            label="via worker"
            title="Kubernetes site: the platform tier has no direct broker endpoint by design. Health and runbook steps go through the site's own worker."
          />
        ),
    },
    {
      id: 'version',
      header: 'Deployment version',
      cell: (row) => row.deployment_manifest_version || <span style={{ color: 'var(--nx-muted)' }}>—</span>,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>Fleet-wide inventory &amp; health</h2>
        {/* The reason this exists: CPU/memory that happen to sit at a
            steady value are indistinguishable from a panel that has
            stopped refreshing. Showing the sample time (and the
            in-flight state) makes "is this live?" answerable without
            having to stare at the numbers waiting for one to twitch. */}
        <span style={{ fontSize: 12, color: 'var(--nx-muted)' }} aria-live="polite">
          {healthQuery.isFetching
            ? 'Sampling health…'
            : healthQuery.isError
              ? 'Health sampling failed — retrying'
              : healthQuery.data
                ? `Health sampled ${new Date(healthQuery.data.queried_at_ms).toLocaleTimeString()} · every 15s`
                : ''}
        </span>
      </div>
      <DataTable
        ariaLabel="Sites"
        columns={columns}
        rows={sites}
        getRowId={(row) => row.site}
        emptyState={<EmptyState title="No sites" description="No sites are registered yet." />}
      />
    </div>
  );
}
